import { decode } from '@msgpack/msgpack';
import { getAuth } from 'firebase/auth';
import { EnvironmentService } from '../env';
import { ErrorService } from '../error';
import { SpinnerService } from '../spinner';
import { SPANISH, UrlService } from '../url';
import { UtilityService } from '../util';
import { FuncActionResult, FuncCallBody, FuncClientFlags } from './interfaces';
import { stringify } from './stringify';

const MAX_ATTEMPTS = 10;


/**
 * This is the base class for the function shims.  Each shim uses the base
 * _call function which makes the remote call and implements any client flag
 * functionality.  To add additional client flags, add them to FuncClientFlags
 * interface and add implementation here.
 */
export class FuncServiceBase {

	constructor(
		private env: EnvironmentService,
		private errorService: ErrorService,
		private spinnerService: SpinnerService,
		private url: UrlService,
		private util: UtilityService,
	) {
	}



	/**
	 * Shim call with no payload.  If the action function has a payload of void, null or undefined
	 * then this shim call will be used instead and the shim will not take a parameter.
	 */
	protected _callNoPayload = <OUT>(path: string, flags: FuncClientFlags = { label: 'LOADING' }): () => Promise<OUT> => {
		const call = this._call<void, OUT>(path, flags);
		return () => call(undefined);
	}


	/**
	 * Shim call with a payload.  The shim will have a single parameter of type IN. The type can
	 * be a JSON object with properties and those values can be accessed through destructuring.
	 */
	protected _call = <IN, OUT>(action: string, flags: FuncClientFlags = { label: 'LOADING' }): (payload: IN) => Promise<OUT> => {

		const spin = !flags.noPageSpinner;

		const shimFunc = async <IN, OUT>(payload: IN): Promise<OUT> => {

			const _handleError = (reject: (reason?: unknown) => void, activity: string, error: string, errorName?: string) => {
				this.errorService.setError(activity, error, errorName, action, payload);
				if (reject) reject(errorName);
			}

			try {
				const auth = getAuth();
				const user = auth.currentUser;
				if (!user) throw new Error('No authenticated user');

				const token = await user.getIdToken();

				return new Promise<OUT>((resolve, reject) => {

					const req = new XMLHttpRequest();
					req.responseType = 'arraybuffer'; // CRITICAL: Tells the browser to treat response as binary
					let attempt = 1;


					//
					// The network request returned back successfully, but there may still be an application error
					//
					req.onload = () => {

						if (spin) this.spinnerService.removeSpinner(action);

						if (req.status === 501) {
							//
							// NOTHING TO DO
							//
							// A duplicate attempt was detected in the backend in the checkForDuplicateCall
							// node middleware. The prior call attempt that got through has either already
							// completed and then resolved or rejected the promise or it is still pending
							// and will come back soon.
							//
						}
						else if (req.status === 200) {
							try {
								const response = decode(req.response) as FuncActionResult<OUT>;

								resolve(response.payload);
							}
							catch (error) {

								if (error instanceof Error) {
									_handleError(reject, 'Response FrontEnd Error', error.message);
								}
								else _handleError(reject, 'Response FrontEnd Error Message', error);

							}
						} else {
							// Handle Errors (Status 4xx or 5xx)
							let appError = 'Unknown Error';

							try {
								// Attempt to decode error as MessagePack first
								const response = decode(req.response) as FuncActionResult<string>;
								if (response.errorStack) console.error(response.errorStack);
								appError = response.payload;
							}
							catch {
								// Fallback: If it's not MessagePack (e.g. a raw String from a crash)
								appError = new TextDecoder().decode(req.response) || `HTTP ${req.status}`;
							}

							_handleError(reject, 'Response BackEnd Error', appError || '', `HTTP${req.status} - ${req.statusText}`);
						}
					};

					req.onerror = () => {
						if (attempt < MAX_ATTEMPTS) {
							setTimeout(() => {
								attempt++;
								message.callAttempt = attempt;

								// Re-open and re-send logic (Simplified for brevity)
								req.open('POST', this.env.getEnvironment().functionUrl + 'action', true);
								req.responseType = 'arraybuffer';
								req.setRequestHeader('Authorization', 'Bearer ' + token);
								req.setRequestHeader("Content-Type", "application/json");
								req.send(stringify(message));
							}, attempt * 1000);
						} else {
							if (spin) this.spinnerService.removeSpinner(action);
							_handleError(reject, 'Network Error', 'Failed to Connect', 'Connection Failed');
						}
					};

					req.onabort = () => {
						if (spin) this.spinnerService.removeSpinner(action);
						reject(`func.${action}() aborted!`);
					}

					req.open('POST', this.env.getEnvironment().functionUrl + 'action', true);
					req.setRequestHeader('Authorization', 'Bearer ' + token);
					req.setRequestHeader("Content-Type", "application/json");

					// Spinner label logic
					const spinLabel = this.getLabel(flags.label);
					if (spin) this.spinnerService.addSpinner(action, spinLabel);

					const message: FuncCallBody<IN> = {
						payload,
						languageId: this.url.languageId(),
						action,
						callAttempt: attempt,
					};

					req.send(stringify(message));
				});
			}
			catch (error: any) {
				_handleError(() => undefined, 'Function Prep Error', error.message);
				return Promise.reject('Error preparing call');
			}
		}

		return shimFunc;
	}

	private getLabel(label: string): string {
		const isSpanish = this.url.languageId() === SPANISH;
		const labels: any = {
			ADDING: isSpanish ? 'Añadiendo' : 'Adding',
			DELETING: isSpanish ? 'Eliminando' : 'Deleting',
			LOADING: isSpanish ? 'Cargando' : 'Loading',
			PROCESSING: isSpanish ? 'Tratamiento' : 'Processing',
			UPDATING: isSpanish ? 'Actualizando' : 'Updating',
		};
		return labels[label] || 'Loading';
	}
}